// src/pages/MisPedidos.js
import React, { useEffect, useState } from 'react';
import { supabase } from '../supabaseClient';
import { routes } from '../routes';

// Moneda CRC
const formatCRC = (n) =>
  `₡${Number(n || 0).toLocaleString('es-CR', { minimumFractionDigits: 0 })}`;

// Normaliza cart (jsonb / string / objeto)
const normalizeCart = (cart) => {
  try {
    if (!cart) return [];
    if (Array.isArray(cart)) return cart;
    if (typeof cart === 'string') return JSON.parse(cart);
    if (cart && Array.isArray(cart.items)) return cart.items;
  } catch (e) {
    console.warn('No se pudo parsear cart:', e);
  }
  return [];
};

// Fecha legible
const formatDate = (iso) => {
  try {
    if (!iso) return '';
    const d = new Date(iso);
    return d.toLocaleString('es-CR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return '';
  }
};

// ID corto para UI (UUID -> primeros 8; numérico -> últimos 6)
const shortOrderId = (id) => {
  if (id == null) return '';
  const s = String(id);
  if (s.includes('-')) return s.slice(0, 8); // UUID
  if (/^\d+$/.test(s)) return s.slice(-6);   // numérico
  return s.slice(0, 8);
};

export default function MisPedidosPage({ user, onNavigate }) {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(0);
  const pageSize = 10;
  const [hasMore, setHasMore] = useState(false);
  const [expanded, setExpanded] = useState({}); // id -> bool

  useEffect(() => {
    if (!user) return;

    const fetchPage = async () => {
      setLoading(true);
      setError('');
      try {
        const from = page * pageSize;
        const to = from + pageSize - 1;

        // Usamos tus campos reales: attended y factura_numero
        const { data, error, count } = await supabase
          .from('orders')
          .select(
            'id, created_at, total, payment_method, cart, attended, factura_numero, cliente_id',
            { count: 'exact' }
          )
          .eq('cliente_id', user.id)
          .order('created_at', { ascending: false })
          .range(from, to);

        if (error) throw error;

        setOrders((prev) => (page === 0 ? data || [] : [...prev, ...(data || [])]));
        if (typeof count === 'number') {
          setHasMore(to + 1 < count);
        } else {
          setHasMore((data || []).length === pageSize);
        }
      } catch (e) {
        console.error('Supabase error:', e);
        setError(e?.message || 'No pudimos cargar tus pedidos.');
      } finally {
        setLoading(false);
      }
    };

    fetchPage();
  }, [user, page]); // deps estándar (sin reglas especiales)

  const toggle = (id) => setExpanded((p) => ({ ...p, [id]: !p[id] }));

  if (!user) {
    return (
      <div className="container mx-auto px-4 sm:px-6 py-12">
        <div className="max-w-md mx-auto bg-white rounded-2xl shadow-lg p-8 text-center">
          <h1 className="text-2xl font-bold mb-3">Debes iniciar sesión</h1>
          <p className="text-gray-600 mb-6">
            Para ver tus pedidos, inicia sesión con tu cuenta.
          </p>
          <div className="flex gap-3 justify-center">
            <button
              onClick={() => onNavigate && onNavigate(routes.login)}
              className="bg-[#5A61E5] text-white px-5 py-3 rounded-lg hover:bg-[#4A51D5]"
            >
              Ir a login
            </button>
            <button
              onClick={() => onNavigate && onNavigate(routes.registro)}
              className="bg-gray-200 text-gray-800 px-5 py-3 rounded-lg hover:bg-gray-300"
            >
              Registrarme
            </button>
          </div>
        </div>
      </div>
    );
  }



  return (
    <div className="container mx-auto px-4 sm:px-6 py-10">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-3xl font-extrabold text-gray-900">Mis pedidos</h1>
        <div className="flex items-center gap-3">
         
          <span className="text-xs text-gray-500">ID cliente: {`CLI${String(user.id).padStart(4, '0')}`}</span>

        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4">
          {error}
        </div>
      )}

      {loading && page === 0 ? (
        <div className="bg-white rounded-2xl shadow p-6 text-center">Cargando…</div>
      ) : orders.length === 0 ? (
        <div className="bg-white rounded-2xl shadow p-8">
          <p className="text-gray-700">No encontramos pedidos asociados a tu cuenta.</p>
          <ul className="list-disc pl-5 text-sm text-gray-600 mt-2 space-y-1">
            <li>Verifica que tus compras recientes se hayan hecho iniciando sesión.</li>
            <li>
              Los pedidos hechos sin sesión pueden tener <code>cliente_id</code> nulo
              y no aparecen aquí.
            </li>
          </ul>
          <div className="mt-4 flex gap-3">
            <button
              onClick={() => onNavigate && onNavigate(routes.home)}
              className="bg-[#5A61E5] text-white px-5 py-3 rounded-lg hover:bg-[#4A51D5]"
            >
              Seguir comprando
            </button>
          
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          {orders.map((o) => {
            const items = normalizeCart(o.cart);
            const itemCount = items.reduce(
              (s, it) => s + Number(it.quantity || 0),
              0
            );

            return (
              <div key={o.id} className="bg-white rounded-2xl shadow border p-5">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                  <div>
                    <div className="text-sm text-gray-500">Pedido</div>
                    <div className="text-lg font-semibold">#{shortOrderId(o.id)}</div>
                    {o.created_at && (
                      <div className="text-xs text-gray-500">{formatDate(o.created_at)}</div>
                    )}
                  </div>

                  <div className="md:text-center">
                    <div className="text-sm text-gray-500">Total</div>
                    <div className="text-xl font-bold">{formatCRC(o.total)}</div>
                    {itemCount > 0 && (
                      <div className="text-xs text-gray-500">{itemCount} artículos</div>
                    )}
                  </div>

                  <div className="md:text-right space-y-1">
                    {/* Factura electrónica */}
                    <div className="text-sm">
                      <span className="text-gray-500">Factura:</span>{' '}
                      {o.factura_numero ? (
                        <span className="font-mono text-gray-800">{o.factura_numero}</span>
                      ) : (
                        <span className="text-amber-700 font-medium">Pendiente</span>
                      )}
                    </div>

                    {/* Estado atendido */}
                    {o.attended ? (
                      <span className="inline-block px-2 py-1 text-xs rounded bg-green-50 text-green-700 border border-green-200">
                        Atendido
                      </span>
                    ) : (
                      <span className="inline-block px-2 py-1 text-xs rounded bg-amber-50 text-amber-700 border border-amber-200">
                        Pendiente
                      </span>
                    )}

                    {/* Método de pago si existe */}
                    {o.payment_method && (
                      <div className="text-sm text-gray-700">
                        <span className="text-gray-500">Pago:</span> {o.payment_method}
                      </div>
                    )}
                  </div>
                </div>

                {/* Detalle */}
                <div className="mt-4">
                  <button
                    onClick={() => toggle(o.id)}
                    className="text-sm text-[#5A61E5] hover:underline"
                  >
                    {expanded[o.id] ? 'Ocultar detalle' : 'Ver detalle'}
                  </button>

                  {expanded[o.id] && (
                    <div className="mt-3 overflow-x-auto">
                      <table className="min-w-full text-sm">
                        <thead>
                          <tr className="text-left text-gray-500 border-b">
                            <th className="py-2 pr-2">Producto</th>
                            <th className="py-2 pr-2">Cantidad</th>
                            <th className="py-2 pr-2">Precio</th>
                            <th className="py-2 pr-2">Subtotal</th>
                          </tr>
                        </thead>
                        <tbody>
                          {items.map((it, idx) => (
                            <tr key={idx} className="border-b last:border-0">
                              <td className="py-2 pr-2 flex items-center gap-3">
                                {it.image && (
                                  <img
                                    src={it.image}
                                    alt={it.name}
                                    className="w-10 h-10 rounded object-cover"
                                  />
                                )}
                                <span className="font-medium text-gray-800">{it.name}</span>
                              </td>
                              <td className="py-2 pr-2">{it.quantity}</td>
                              <td className="py-2 pr-2">{formatCRC(it.price)}</td>
                              <td className="py-2 pr-2">
                                {formatCRC((it.price || 0) * (it.quantity || 0))}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Paginación */}
      {orders.length > 0 && hasMore && (
        <div className="text-center mt-6">
          <button
            onClick={() => setPage((p) => p + 1)}
            className="px-6 py-3 rounded-lg border hover:bg-gray-50"
          >
            Cargar más
          </button>
        </div>
      )}
    </div>
  );
}
